import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Link,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import PageHeader from 'components/PageHeader';
import buildRoute from 'components/buildRoute';
import EmptyStatePlaceholder from 'components/EmptyStatePlaceholder';
import TableContainerWithScrollShadow from 'components/TableContainerWithScrollShadow';
import { createRecord, fetchList, ReauthRequiredError } from './registryApi';
import { EXPERIMENT_STATUS } from './registryChoices';
import { formatDateTime } from './registryFormat';

// The serializer blanks the registering org on anonymous experiments shared with another org.
const orgLabel = (experiment) =>
  experiment.registering_organization_name || (experiment.anonymous ? 'Anonymous' : '—');

// Reference docs from the Analyst Institute, shown to every registry user.
const REGISTRY_RESOURCE_LINKS = [
  {
    label: 'User guide',
    href: 'https://drive.google.com/file/d/1l7PbxPJOn1y0ezmDnTkLLk0cRlt723wz/view?usp=sharing',
  },
  {
    label: 'Data access, governance, and community norms',
    href: 'https://drive.google.com/file/d/1E3AlctnWwkcB9zxC_5HKNzra-1zjdSEK/view?usp=drive_link',
  },
];
import RegistryStatusChip from './components/RegistryStatusChip';
import { lastStep } from './wizard/draftProgress';

const orgStorageKey = (projectId) => `registrySelectedOrg_${projectId}`;

const readStoredOrg = (projectId) => {
  try {
    return sessionStorage.getItem(orgStorageKey(projectId)) || '';
  } catch {
    return '';
  }
};

const RegistryDashboardPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [experiments, setExperiments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingExperiments, setLoadingExperiments] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoadingOrgs(true);
      try {
        const orgs = (await fetchList('/registry/my-organizations')) || [];
        setOrganizations(orgs);
        const stored = readStoredOrg(projectId);
        const initial = orgs.find((org) => org.id === stored) ? stored : orgs[0]?.id || '';
        setSelectedOrgId(initial);
      } catch (e) {
        setError('Could not load your organizations.');
      } finally {
        setLoadingOrgs(false);
      }
    })();
  }, [projectId]);

  // One organization's view at a time: its own experiments plus any shared with it.
  const loadExperiments = useCallback(async () => {
    if (!selectedOrgId) return;
    setLoadingExperiments(true);
    setError('');
    try {
      const data = await fetchList(`/registry/my-experiments?organization=${selectedOrgId}`);
      setExperiments(data || []);
    } catch (e) {
      if (e instanceof ReauthRequiredError) {
        window.location.href = e.reauthUrl;
        return;
      }
      setError(e.message || 'Could not load experiments.');
    } finally {
      setLoadingExperiments(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    loadExperiments();
  }, [loadExperiments]);

  const handleOrgChange = (orgId) => {
    setSelectedOrgId(orgId);
    try {
      sessionStorage.setItem(orgStorageKey(projectId), orgId);
    } catch {
      // per-tab convenience only
    }
  };

  const handleRegisterNew = async () => {
    setCreating(true);
    setError('');
    try {
      const experiment = await createRecord('/registry/my-experiments', {
        registering_organization: selectedOrgId,
      });
      navigate(`/projects/${projectId}/registry/${experiment.id}/edit/step1`);
    } catch (e) {
      setError('Could not start a new registration.');
      setCreating(false);
    }
  };

  const detailPath = (experiment) =>
    experiment.status === EXPERIMENT_STATUS.DRAFT ||
    experiment.status === EXPERIMENT_STATUS.NEEDS_UPDATES
      ? `/projects/${projectId}/registry/${experiment.id}/edit/${lastStep(experiment.id)}`
      : `/projects/${projectId}/registry/${experiment.id}?organization=${selectedOrgId}`;

  const filteredExperiments = experiments.filter((experiment) => {
    const query = searchQuery.toLowerCase();
    return (
      (experiment.experiment_name || '').toLowerCase().includes(query) ||
      (experiment.registry_id || '').toLowerCase().includes(query) ||
      orgLabel(experiment).toLowerCase().includes(query)
    );
  });

  const orgAndRegister = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {organizations.length > 0 && (
        <TextField
          select
          size="small"
          label="Organization"
          value={selectedOrgId}
          onChange={(e) => handleOrgChange(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          {organizations.map((org) => (
            <MenuItem key={org.id} value={org.id}>
              {org.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Button
        variant="contained"
        color="secondary"
        onClick={handleRegisterNew}
        disabled={creating || !selectedOrgId}
      >
        Register New Experiment
      </Button>
    </Box>
  );

  // Reference docs as a quiet line under the subtitle, apart from the actions.
  const resourcesLine = (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
      Resources:{' '}
      {REGISTRY_RESOURCE_LINKS.map((link, index) => (
        <span key={link.href}>
          {index > 0 && ' · '}
          <Link
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
          >
            {link.label}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </span>
      ))}
    </Typography>
  );

  if (loadingOrgs) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Research Registry"
        subtitle="Experiments submitted by or shared with your organization."
        action={orgAndRegister}
      />
      {resourcesLine}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {organizations.length === 0 ? (
        <EmptyStatePlaceholder
          title="You're not part of a registry organization yet"
          subtitle="Contact the Analyst Institute to be added to your organization."
          sx={{ mt: 3 }}
        />
      ) : (
        <>
          <Box sx={{ mt: 3, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              size="small"
              hiddenLabel
              placeholder="Search experiments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: '100%',
                maxWidth: 420,
                // No label here, so use MUI's own legend height: the theme's taller legend
                // (sized for labeled fields) drops the top border below the field's background.
                '& .MuiOutlinedInput-notchedOutline legend': { lineHeight: '11px' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
            {/* Refresh pushed to the far right, lined up with the table's edge. */}
            <Tooltip title="Refresh">
              <span style={{ marginLeft: 'auto' }}>
                <IconButton
                  size="small"
                  onClick={loadExperiments}
                  disabled={loadingExperiments || !selectedOrgId}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {loadingExperiments ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : experiments.length === 0 ? (
            <EmptyStatePlaceholder
              title="No experiments yet"
              subtitle="Register a new experiment to get started."
            />
          ) : (
            // Long lists scroll inside the table (header stays put), like SyncSchedulePage.
            <TableContainerWithScrollShadow sx={{ maxHeight: '70vh' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Experiment</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>Experiment ID</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredExperiments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No experiments match your search.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredExperiments.map((experiment) => (
                      <TableRow key={experiment.id} hover>
                        <TableCell sx={{ fontWeight: 500, color: 'text.dark' }}>
                          {experiment.experiment_name || 'Untitled draft'}
                        </TableCell>
                        <TableCell>{orgLabel(experiment)}</TableCell>
                        <TableCell>{experiment.registry_id || '—'}</TableCell>
                        <TableCell>{formatDateTime(experiment.submitted_at) || '—'}</TableCell>
                        <TableCell>{formatDateTime(experiment.updated_at) || '—'}</TableCell>
                        <TableCell>
                          <RegistryStatusChip status={experiment.status} />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="text"
                            size="small"
                            component={RouterLink}
                            to={detailPath(experiment)}
                            sx={{ textDecoration: 'underline', minWidth: 'auto' }}
                          >
                            {experiment.status === EXPERIMENT_STATUS.NEEDS_UPDATES
                              ? 'Edit Details'
                              : experiment.status === EXPERIMENT_STATUS.DRAFT
                                ? 'Continue'
                                : 'View Details'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainerWithScrollShadow>
          )}
        </>
      )}
    </Box>
  );
};

const routeArgs = {
  element: <RegistryDashboardPage />,
};

export default buildRoute(routeArgs);
